import { useMemo, useState } from 'react'
import { Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Breadcrumbs,
  Button,
  Grid,
  Link,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material'
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'
import { PageShell } from '@components/common/PageShell'
import { DataTable, DataTableColumn } from '@components/common/DataTable'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import type { CrashGroup, CrashReportSummary } from '@api/types/crashReports'
import { useClearCrashGroup, useCrashGroups, useCrashReports } from '../hooks/useCrashReports'
import { CrashKindChip } from '../components/CrashKindChip'
import { CrashReportDialog } from '../components/CrashReportDialog'
import {
  crashKindLabel,
  formatDateTime,
  formatPlatform,
  formatRelative,
  formatVersion,
} from '../components/crashFormat'

/**
 * One crash group: its summary, a "mark fixed" action, and every report in it.
 *
 * There is no single-group endpoint, so the header is read from the groups list (the row the user
 * clicked arrives in router state; a pasted link falls back to the 90-day list). If the group has
 * aged out of that, the header is rebuilt from the newest report instead.
 */
export default function CrashGroupPage() {
  const { fingerprint = '' } = useParams<{ fingerprint: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const backDays = Number(searchParams.get('days')) || 30
  const stateGroup = (location.state as { group?: CrashGroup } | null)?.group

  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  const [openId, setOpenId] = useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  const groups = useCrashGroups({ days: 90 })
  const reports = useCrashReports({ fingerprint, page: page + 1, pageSize })
  const clear = useClearCrashGroup()

  const group: CrashGroup | undefined =
    groups.data?.find((g) => g.fingerprint === fingerprint) ?? stateGroup
  const newest = reports.data?.items[0]
  const kind = group?.kind ?? newest?.kind ?? 'Other'
  const message = group?.message ?? newest?.message ?? ''

  const listPath = `/crashes?days=${backDays}`

  const columns = useMemo<DataTableColumn<CrashReportSummary>[]>(
    () => [
      {
        key: 'time',
        header: 'Time',
        primary: true,
        render: (r) => (
          <Stack spacing={0}>
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
              {formatDateTime(r.occurredAt)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatRelative(r.occurredAt)}
            </Typography>
          </Stack>
        ),
      },
      {
        key: 'user',
        header: 'User',
        render: (r) =>
          r.userId ? (
            <Link
              component={RouterLink}
              to={`/users/${r.userId}`}
              variant="body2"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              {r.userDisplayName || r.userId}
            </Link>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Signed out
            </Typography>
          ),
      },
      {
        key: 'device',
        header: 'Device',
        render: (r) => (
          <Stack spacing={0}>
            <Typography variant="body2">{r.deviceModel || '—'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {formatPlatform(r.platform)} {r.osVersion ?? ''}
            </Typography>
          </Stack>
        ),
      },
      {
        key: 'version',
        header: 'App',
        render: (r) => (
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
            {formatVersion(r.appVersion, r.buildNumber)}
          </Typography>
        ),
      },
      {
        key: 'route',
        header: 'Route',
        hideBelow: 'md',
        render: (r) => (
          <Typography
            variant="body2"
            sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, wordBreak: 'break-all' }}
          >
            {r.route || '—'}
          </Typography>
        ),
      },
    ],
    [],
  )

  const confirmClear = () => {
    clear.mutate(fingerprint, {
      onSuccess: () => {
        setConfirmOpen(false)
        navigate(listPath, { replace: true })
      },
      onError: () => {
        setConfirmOpen(false)
        setToast('Could not clear this crash group.')
      },
    })
  }

  const breadcrumbs = (
    <Breadcrumbs>
      <Link component={RouterLink} to={listPath} underline="hover" color="inherit">
        Crashes
      </Link>
      <span>{crashKindLabel(kind)}</span>
    </Breadcrumbs>
  )

  return (
    <PageShell
      title="Crash group"
      breadcrumbs={breadcrumbs}
      actions={
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteSweepIcon />}
          onClick={() => setConfirmOpen(true)}
          disabled={clear.isPending}
        >
          Mark fixed and clear reports
        </Button>
      }
    >
      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, minWidth: 0 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CrashKindChip kind={kind} size="medium" />
          </Stack>
          <Typography variant="body1" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap', fontWeight: 500 }}>
            {message || '(no message)'}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Stat label="Reports" value={group ? group.count.toLocaleString() : reports.data?.total.toLocaleString() ?? '—'} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Stat label="Affected users" value={group ? group.affectedUsers.toLocaleString() : '—'} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Stat label="First seen" value={formatRelative(group?.firstSeen)} detail={group?.firstSeen ? formatDateTime(group.firstSeen) : undefined} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Stat
                label="Last seen"
                value={formatRelative(group?.lastSeen ?? newest?.occurredAt)}
                detail={(group?.lastSeen ?? newest?.occurredAt) ? formatDateTime(group?.lastSeen ?? newest?.occurredAt) : undefined}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Stat label="Latest build" value={formatVersion(group?.latestAppVersion, group?.latestBuildNumber)} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Stat label="Platforms" value={group?.platforms.length ? group.platforms.map(formatPlatform).join(', ') : '—'} />
            </Grid>
          </Grid>
        </Stack>
      </Paper>

      <DataTable
        rows={reports.data?.items ?? []}
        columns={columns}
        getRowKey={(r) => r.id}
        loading={reports.isLoading}
        error={reports.isError ? 'Could not load reports for this crash.' : null}
        onRetry={() => reports.refetch()}
        emptyMessage="No reports left in this group. It may have been cleared."
        page={page}
        pageSize={pageSize}
        total={reports.data?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(0)
        }}
        onRowClick={(r) => setOpenId(r.id)}
      />

      <CrashReportDialog id={openId} onClose={() => setOpenId(null)} />

      <ConfirmDialog
        open={confirmOpen}
        title="Mark this crash fixed?"
        message="Use this once a fix has shipped. Every report in this group is permanently deleted and you go back to the crash list. If the bug is still there, new reports will start a fresh group, so you can tell whether the fix worked."
        confirmLabel="Delete reports"
        destructive
        loading={clear.isPending}
        onConfirm={confirmClear}
        onClose={() => setConfirmOpen(false)}
      />

      <Snackbar
        open={toast != null}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>
    </PageShell>
  )
}

/** `detail` is shown as a caption under the value (the exact time behind "3 days ago"), not a tooltip, so it reads on a phone. */
function Stat({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <Stack spacing={0.25}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight="bold">
        {value}
      </Typography>
      {detail && (
        <Typography variant="caption" color="text.secondary">
          {detail}
        </Typography>
      )}
    </Stack>
  )
}
